import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Abas,
  Alerta,
  Bloco,
  BotaoDocumento,
  HeroModulo,
  Campo,
  CAMPO_SIM,
  Cascata,
  Casos,
  type CasoPratico,
  Linha,
  Referencias,
  Tabela,
  abrirDocumento,
} from "../componentes/HubDpUi";
import { reais, type ModeloIrrf, type ParametrosFolha } from "../lib/folhaDp";
import { calcularFerias, calcularDecimo, MAX_DIAS_ABONO } from "../lib/feriasDecimoDp";
import { avisoFerias } from "../lib/documentosDp";
import { mascararMoeda, mascararInteiro, mascararCnpj, mascararCpf, mascararData, moedaDeNumero } from "../lib/mascaras";
import { REFERENCIA_FERIAS } from "../componentes/HubDpTextos";

// O módulo Férias e 13º. Duas calculadoras num módulo só, porque na prática são
// a mesma conversa: o 13º costuma ser adiantado junto das férias, e o aluno
// compara os dois lado a lado.

const BASE = "/ferramenta/hub-dp/ferias-13";
const ABAS = [
  { slug: "", rotulo: "Férias" },
  { slug: "decimo", rotulo: "13º salário" },
  { slug: "aviso", rotulo: "Aviso de férias" },
  { slug: "referencia", rotulo: "Referência" },
  { slug: "casos", rotulo: "Casos práticos" },
];

interface Form {
  // Férias
  salarioBase: string;
  mediaVariaveis: string;
  diasGozo: string;
  venderDias: boolean;
  diasVendidos: string;
  dependentes: string;
  modeloIrrf: ModeloIrrf;
  adiantarDecimo: boolean;
  // 13º
  avos: string;
  /** Vazio = não informado (assume a metade). "0" = não houve adiantamento. */
  adiantamentoRecebido: string;
  // Aviso
  empresa: string;
  cnpj: string;
  cidade: string;
  empregado: string;
  cpf: string;
  cargo: string;
  periodoAquisitivo: string;
  inicioGozo: string;
}

const VAZIO: Form = {
  salarioBase: "",
  mediaVariaveis: "",
  diasGozo: "30",
  venderDias: false,
  diasVendidos: "10",
  dependentes: "",
  modeloIrrf: "completo",
  adiantarDecimo: false,
  avos: "12",
  adiantamentoRecebido: "",
  empresa: "",
  cnpj: "",
  cidade: "",
  empregado: "",
  cpf: "",
  cargo: "",
  periodoAquisitivo: "",
  inicioGozo: "",
};

const num = (t: string): number => mascararMoeda(t).numero ?? 0;
const int = (t: string): number => Number(t.replace(/\D+/g, "")) || 0;

const CASOS: CasoPratico<Partial<Form>>[] = [
  {
    nome: "30 dias de férias, sem adicionais",
    descricao: "R$ 3.800 de base. Veja o terço constitucional e o INSS sobre férias + terço.",
    valores: { salarioBase: moedaDeNumero(3800), diasGozo: "30", dependentes: "1" },
  },
  {
    nome: "20 dias de gozo + 10 vendidos",
    descricao:
      "R$ 5.500 com R$ 600 de médias. O abono e o terço dele NÃO entram na base de INSS e IRRF — compare o bruto com a base tributável.",
    valores: {
      salarioBase: moedaDeNumero(5500),
      mediaVariaveis: moedaDeNumero(600),
      diasGozo: "20",
      venderDias: true,
      diasVendidos: "10",
      modeloIrrf: "simplificado",
    },
  },
  {
    nome: "Férias com a 1ª parcela do 13º",
    descricao:
      "R$ 4.200, 30 dias, adiantando o 13º. O adiantamento é metade da REMUNERAÇÃO (Lei 4.749/65), e não sofre desconto agora.",
    valores: { salarioBase: moedaDeNumero(4200), diasGozo: "30", adiantarDecimo: true },
  },
  {
    nome: "13º integral, 12 avos",
    descricao: "R$ 4.200 e 2 dependentes. Os descontos incidem sobre o 13º INTEGRAL, na 2ª parcela.",
    valores: { salarioBase: moedaDeNumero(4200), avos: "12", dependentes: "2" },
  },
  {
    nome: "13º proporcional, admitido em junho",
    descricao: "7 avos de R$ 3.000. Mês com 15 dias ou mais de trabalho conta um avo inteiro.",
    valores: { salarioBase: moedaDeNumero(3000), avos: "7" },
  },
  {
    nome: "13º sem adiantamento (admitido em dezembro)",
    descricao:
      "R$ 2.500, 1 avo, e o adiantamento informado como ZERO. É diferente de deixar em branco: quem não recebeu a 1ª parcela não pode ter a 2ª descontada dela.",
    valores: { salarioBase: moedaDeNumero(2500), avos: "1", adiantamentoRecebido: "R$ 0,00" },
  },
];

export default function DpFerias({ p, aba }: { p: ParametrosFolha; aba: string }) {
  const [f, setF] = useState<Form>(VAZIO);
  const [bloqueado, setBloqueado] = useState(false);
  const navegar = useNavigate();
  const troca = <K extends keyof Form>(c: K, v: Form[K]) => setF((a) => ({ ...a, [c]: v }));

  const base = num(f.salarioBase);
  const temBase = base > 0;

  const ferias = temBase
    ? calcularFerias(
        {
          salarioBase: base,
          mediaVariaveis: num(f.mediaVariaveis),
          diasGozo: int(f.diasGozo),
          venderDias: f.venderDias,
          diasVendidos: int(f.diasVendidos),
          dependentes: int(f.dependentes),
          modeloIrrf: f.modeloIrrf,
          adiantarDecimo: f.adiantarDecimo,
        },
        p,
      )
    : null;

  const decimo = temBase
    ? calcularDecimo(
        {
          salarioBase: base,
          mediaVariaveis: num(f.mediaVariaveis),
          avos: int(f.avos),
          dependentes: int(f.dependentes),
          modeloIrrf: f.modeloIrrf,
          // Campo VAZIO é `null` (não informado → assume a metade); campo com
          // "R$ 0,00" é zero de verdade. As duas coisas são diferentes, e é a
          // correção que este porte faz sobre a origem.
          adiantamentoRecebido: f.adiantamentoRecebido.trim() === "" ? null : num(f.adiantamentoRecebido),
        },
        p,
      )
    : null;

  return (
    <>
      <HeroModulo
        titulo="Férias e 13º"
        subtitulo="Férias com o terço constitucional e o abono, e o 13º em avos com as duas parcelas."
        heroi={
          aba === "decimo" && decimo
            ? {
                rotulo: "2ª parcela líquida",
                valor: reais(decimo.segundaParcela.liquido),
                detalhe: `13º de ${decimo.avos} avos: ${reais(decimo.bruto)} bruto`,
              }
            : ferias
              ? {
                  rotulo: "Líquido das férias",
                  valor: reais(ferias.liquido),
                  detalhe: `${reais(ferias.totalBruto)} bruto − ${reais(ferias.inss.valor + ferias.irrf.aplicado.valor)} de descontos`,
                }
              : null
        }
        vazio="Informe o salário base e o líquido aparece aqui, conforme você digita."
      />
      <Abas base={BASE} abas={ABAS} />

      {aba === "" && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,24rem)_1fr]">
          <form id="dados-das-ferias" className="surface h-fit space-y-5 p-5 lg:sticky lg:top-6" onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Salário base">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={f.salarioBase}
                onChange={(e) => troca("salarioBase", mascararMoeda(e.target.value).texto)}
              />
            </Campo>
            <Campo
              rotulo="Médias de variáveis"
              ajuda="Média de horas extras, comissões e adicionais do período aquisitivo. Entram na remuneração das férias."
            >
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={f.mediaVariaveis}
                onChange={(e) => troca("mediaVariaveis", mascararMoeda(e.target.value).texto)}
              />
            </Campo>
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Dias de gozo" ajuda="De 1 a 30.">
                <input
                  className={CAMPO_SIM}
                  inputMode="numeric"
                  value={f.diasGozo}
                  onChange={(e) => troca("diasGozo", mascararInteiro(e.target.value, 2))}
                />
              </Campo>
              <Campo rotulo="Dependentes">
                <input
                  className={CAMPO_SIM}
                  inputMode="numeric"
                  placeholder="0"
                  value={f.dependentes}
                  onChange={(e) => troca("dependentes", mascararInteiro(e.target.value, 2))}
                />
              </Campo>
            </div>

            <label className="flex items-center gap-2.5 text-sm text-tinta">
              <input
                type="checkbox"
                className="size-4 accent-laranja"
                checked={f.venderDias}
                onChange={(e) => troca("venderDias", e.target.checked)}
              />
              Vender dias (abono pecuniário)
            </label>
            {f.venderDias && (
              <Campo rotulo="Dias vendidos" ajuda={`No máximo ${MAX_DIAS_ABONO}, que é 1/3 dos 30 (CLT art. 143).`}>
                <input
                  className={CAMPO_SIM}
                  inputMode="numeric"
                  value={f.diasVendidos}
                  onChange={(e) => troca("diasVendidos", mascararInteiro(e.target.value, 2))}
                />
              </Campo>
            )}

            <label className="flex items-center gap-2.5 text-sm text-tinta">
              <input
                type="checkbox"
                className="size-4 accent-laranja"
                checked={f.adiantarDecimo}
                onChange={(e) => troca("adiantarDecimo", e.target.checked)}
              />
              Adiantar a 1ª parcela do 13º
            </label>

            <ModeloIrrfCampo nome="ferias" valor={f.modeloIrrf} aoTrocar={(v) => troca("modeloIrrf", v)} />
          </form>

          <div id="resultado" className="space-y-4">
            {!ferias ? (
              <VazioAviso />
            ) : (
              <>
                <Bloco titulo="Composição" destaque>
                  <Linha rotulo="Remuneração das férias" valor={reais(ferias.remuneracao)} />
                  <Linha rotulo={`Férias (${int(f.diasGozo)} dias)`} valor={reais(ferias.ferias)} />
                  <Linha rotulo="Terço constitucional" valor={reais(ferias.terco)} />
                  {ferias.abono > 0 && (
                    <>
                      <Linha
                        rotulo="Abono pecuniário"
                        valor={reais(ferias.abono)}
                        ajuda="verba indenizatória — não sofre INSS nem IRRF"
                      />
                      <Linha rotulo="Terço do abono" valor={reais(ferias.tercoAbono)} />
                    </>
                  )}
                  {ferias.adiantamentoDecimo > 0 && (
                    <Linha
                      rotulo="1ª parcela do 13º"
                      valor={reais(ferias.adiantamentoDecimo)}
                      ajuda="metade da remuneração, sem descontos agora"
                    />
                  )}
                </Bloco>

                {ferias.abonoLimitado && (
                  <Alerta tom="atencao" titulo="O abono foi limitado a 10 dias">
                    A CLT permite converter no máximo 1/3 do período — 10 dos 30 dias (art. 143). O
                    cálculo acima usou 10.
                  </Alerta>
                )}

                <Bloco titulo="Descontos">
                  <Linha
                    rotulo="Base tributável"
                    valor={reais(ferias.baseTributavel)}
                    ajuda="só férias + terço. O abono e o terço dele ficam fora."
                  />
                  <Linha rotulo="INSS" valor={reais(ferias.inss.valor)} />
                  <Linha
                    rotulo="IRRF"
                    valor={reais(ferias.irrf.aplicado.valor)}
                    ajuda="imposto puro de tabela — a redução do IR de 2026 não é aplicada a férias"
                  />
                </Bloco>

                {/* A cascata no lugar de repetir o líquido, que já está no hero:
                    é ela que mostra o abono como fatia FORA da parte tributada. */}
                <Bloco titulo="De que o bruto é feito">
                  <Cascata
                    rotuloTotal="Total bruto"
                    total={ferias.totalBruto}
                    fatias={[
                      { rotulo: "Férias gozadas", valor: ferias.ferias, cor: "azul" },
                      { rotulo: "Terço constitucional", valor: ferias.terco, cor: "laranja" },
                      {
                        rotulo: "Abono pecuniário",
                        valor: ferias.abono + ferias.tercoAbono,
                        cor: "sucesso",
                        detalhe: "com o terço — fora da base de INSS e IRRF",
                      },
                      {
                        rotulo: "1ª parcela do 13º",
                        valor: ferias.adiantamentoDecimo,
                        cor: "cinza",
                        detalhe: "adiantamento, sem desconto agora",
                      },
                    ]}
                  />
                </Bloco>

                <Alerta tom="informacao" titulo="Prazo do pagamento">
                  Até 2 dias antes do início do gozo (CLT art. 145). Pagar junto da folha do mês é
                  uma das autuações mais comuns.
                </Alerta>
              </>
            )}
          </div>
        </div>
      )}

      {aba === "decimo" && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,24rem)_1fr]">
          <form id="dados-do-decimo" className="surface h-fit space-y-5 p-5 lg:sticky lg:top-6" onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Salário base">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={f.salarioBase}
                onChange={(e) => troca("salarioBase", mascararMoeda(e.target.value).texto)}
              />
            </Campo>
            <Campo rotulo="Médias de variáveis">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={f.mediaVariaveis}
                onChange={(e) => troca("mediaVariaveis", mascararMoeda(e.target.value).texto)}
              />
            </Campo>
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Avos" ajuda="Mês com 15 dias ou mais de trabalho conta 1 avo (Lei 4.090/62).">
                <input
                  className={CAMPO_SIM}
                  inputMode="numeric"
                  value={f.avos}
                  onChange={(e) => troca("avos", mascararInteiro(e.target.value, 2))}
                />
              </Campo>
              <Campo rotulo="Dependentes">
                <input
                  className={CAMPO_SIM}
                  inputMode="numeric"
                  placeholder="0"
                  value={f.dependentes}
                  onChange={(e) => troca("dependentes", mascararInteiro(e.target.value, 2))}
                />
              </Campo>
            </div>
            <Campo
              rotulo="Adiantamento já recebido"
              ajuda="Deixe em BRANCO para assumir a metade do 13º, que é o caso comum. Informe R$ 0,00 se não houve adiantamento nenhum — quem foi admitido em dezembro, por exemplo."
            >
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="em branco = metade"
                value={f.adiantamentoRecebido}
                onChange={(e) => troca("adiantamentoRecebido", mascararMoeda(e.target.value).texto)}
              />
            </Campo>
            <ModeloIrrfCampo nome="decimo" valor={f.modeloIrrf} aoTrocar={(v) => troca("modeloIrrf", v)} />
          </form>

          <div id="resultado" className="space-y-4">
            {!decimo ? (
              <VazioAviso />
            ) : (
              <>
                <Bloco titulo="O 13º" destaque>
                  <Linha rotulo="Remuneração" valor={reais(decimo.remuneracao)} />
                  <Linha rotulo={`13º de ${decimo.avos} avos`} valor={reais(decimo.bruto)} tom="total" />
                </Bloco>

                <Bloco titulo="1ª parcela">
                  <Linha
                    rotulo="Valor"
                    valor={reais(decimo.primeiraParcela)}
                    ajuda="metade do bruto, SEM descontos — até 30 de novembro"
                  />
                </Bloco>

                <Bloco titulo="2ª parcela" destaque>
                  <Linha
                    rotulo="Adiantamento considerado"
                    valor={reais(decimo.adiantamentoConsiderado)}
                    ajuda={
                      f.adiantamentoRecebido.trim() === ""
                        ? "não informado — assumida a metade"
                        : "informado por você"
                    }
                  />
                  <Linha rotulo="Resta a pagar (bruto)" valor={reais(decimo.segundaParcela.bruto)} />
                  <Linha
                    rotulo="INSS"
                    valor={reais(decimo.segundaParcela.inss.valor)}
                    ajuda="sobre o 13º INTEGRAL, não sobre a metade"
                  />
                  <Linha rotulo="IRRF" valor={reais(decimo.segundaParcela.irrf.aplicado.valor)} />
                  <Linha rotulo="Líquido da 2ª parcela" valor={reais(decimo.segundaParcela.liquido)} tom="total" />
                </Bloco>

                <Bloco titulo="Depósito do empregador">
                  <Linha rotulo="FGTS 8% do 13º" valor={reais(decimo.fgts)} />
                </Bloco>

                <Alerta tom="atencao" titulo="Os prazos">
                  1ª parcela até 30/11 · 2ª parcela até 20/12 · DARF 0561 do IRRF até o 2º dia útil
                  de janeiro. O IRRF do 13º é <strong>exclusivo na fonte</strong>: não se soma ao
                  salário do mês nem entra no ajuste anual.
                </Alerta>
              </>
            )}
          </div>
        </div>
      )}

      {aba === "aviso" && (
        <AvisoAba f={f} troca={troca} ferias={ferias} bloqueado={bloqueado} setBloqueado={setBloqueado} />
      )}

      {aba === "referencia" && (
        <div className="space-y-6">
          {REFERENCIA_FERIAS.map((t) => (
            <Tabela key={t.titulo} tabela={t} />
          ))}
          <Alerta tom="erro" titulo="O abono pecuniário não é tributado">
            Nem o abono nem o terço dele sofrem INSS ou IRRF — é verba indenizatória (Lei 8.212/91,
            art. 28, §9º, "e", 6; Súmula 125 do STJ). Tributá-los é o erro clássico do módulo, e a
            calculadora mostra a base tributável separada do bruto justamente para você ver a
            diferença.
          </Alerta>
          <Alerta tom="atencao" titulo="A regra dos avos">
            <code className="rounded bg-chip px-1.5 py-0.5">13º = (salário + médias) ÷ 12 × avos</code>
            . Um mês só conta se tiver 15 dias ou mais de trabalho. Quem foi admitido em 17 de junho
            tem 6 avos, não 7 — sobram 14 dias em junho.
          </Alerta>
        </div>
      )}

      {aba === "casos" && (
        <Casos
          casos={CASOS}
          aoCarregar={(v) => {
            setF({ ...VAZIO, ...v });
            // Caso de 13º leva para a aba do 13º; caso de férias, para a de
            // férias. Sem isso o aluno carregaria "13º proporcional" e cairia
            // numa tela de férias, sem entender o que mudou.
            navegar(v.avos && v.avos !== "12" ? `${BASE}/decimo` : BASE);
          }}
        />
      )}

      <Referencias inss={p.referenciaInss} irrf={p.referenciaIrrf} />
    </>
  );
}

function VazioAviso() {
  return (
    <div className="rounded-cf border border-dashed border-borda p-6 text-sm leading-relaxed text-cinza">
      Informe o salário base para ver o cálculo. Nada é gravado.
    </div>
  );
}

function ModeloIrrfCampo({
  nome,
  valor,
  aoTrocar,
}: {
  nome: string;
  valor: ModeloIrrf;
  aoTrocar: (v: ModeloIrrf) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-xs font-semibold text-cinza">Modelo do IRRF</legend>
      <div className="space-y-2">
        {(
          [
            ["completo", "Modelo 1 — completo"],
            ["simplificado", "Modelo 2 — simplificado"],
          ] as const
        ).map(([v, rotulo]) => (
          <label key={v} className="flex items-center gap-2.5 text-sm text-tinta">
            <input
              type="radio"
              name={`modelo-irrf-${nome}`}
              className="size-4 accent-laranja"
              checked={valor === v}
              onChange={() => aoTrocar(v)}
            />
            {rotulo}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function AvisoAba({
  f,
  troca,
  ferias,
  bloqueado,
  setBloqueado,
}: {
  f: Form;
  troca: <K extends keyof Form>(c: K, v: Form[K]) => void;
  ferias: ReturnType<typeof calcularFerias> | null;
  bloqueado: boolean;
  setBloqueado: (v: boolean) => void;
}) {
  const dias = int(f.diasGozo);

  // As datas de fim e retorno são DERIVADAS do início e dos dias — o aluno não
  // digita três datas que podem discordar. O primeiro dia conta no período, então
  // o fim é início + (dias − 1).
  const dataDe = (texto: string): Date | null => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto.trim());
    if (!m) return null;
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const br = (d: Date) => d.toLocaleDateString("pt-BR");
  const inicio = dataDe(f.inicioGozo);
  const fim = inicio && dias > 0 ? new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + dias - 1) : null;
  const retorno = inicio && dias > 0 ? new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + dias) : null;

  const gerar = () => {
    const ok = abrirDocumento(
      avisoFerias({
        empresa: f.empresa,
        cnpj: f.cnpj,
        cidade: f.cidade,
        empregado: f.empregado,
        cpf: f.cpf,
        cargo: f.cargo,
        periodoAquisitivo: f.periodoAquisitivo,
        inicioGozo: f.inicioGozo,
        fimGozo: fim ? br(fim) : "",
        retorno: retorno ? br(retorno) : "",
        diasGozo: dias,
        diasVendidos: f.venderDias ? int(f.diasVendidos) : 0,
        adiantaDecimo: f.adiantarDecimo,
        dataEmissao: new Date().toLocaleDateString("pt-BR"),
      }),
    );
    setBloqueado(!ok);
  };

  return (
    <div className="space-y-6">
      <Alerta tom="informacao">
        Os dias de gozo, o abono e o adiantamento do 13º vêm da aba <strong>Férias</strong>. As datas
        de fim e de retorno são calculadas do início — o primeiro dia conta no período.
      </Alerta>

      <form id="dados-do-aviso" className="surface grid gap-4 p-5 sm:grid-cols-2 sm:p-6" onSubmit={(e) => e.preventDefault()}>
        <Campo rotulo="Empresa">
          <input className={CAMPO_SIM} value={f.empresa} onChange={(e) => troca("empresa", e.target.value)} />
        </Campo>
        <Campo rotulo="CNPJ">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={f.cnpj}
            onChange={(e) => troca("cnpj", mascararCnpj(e.target.value))}
          />
        </Campo>
        <Campo rotulo="Empregado">
          <input className={CAMPO_SIM} value={f.empregado} onChange={(e) => troca("empregado", e.target.value)} />
        </Campo>
        <Campo rotulo="CPF">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={f.cpf}
            onChange={(e) => troca("cpf", mascararCpf(e.target.value))}
          />
        </Campo>
        <Campo rotulo="Cargo">
          <input className={CAMPO_SIM} value={f.cargo} onChange={(e) => troca("cargo", e.target.value)} />
        </Campo>
        <Campo rotulo="Cidade">
          <input className={CAMPO_SIM} value={f.cidade} onChange={(e) => troca("cidade", e.target.value)} />
        </Campo>
        <Campo rotulo="Período aquisitivo" ajuda="Os 12 meses que geraram o direito.">
          <input
            className={CAMPO_SIM}
            placeholder="02/03/2025 a 01/03/2026"
            value={f.periodoAquisitivo}
            onChange={(e) => troca("periodoAquisitivo", e.target.value)}
          />
        </Campo>
        <Campo rotulo="Início do gozo">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            placeholder="dd/mm/aaaa"
            value={f.inicioGozo}
            onChange={(e) => troca("inicioGozo", mascararData(e.target.value))}
          />
        </Campo>
      </form>

      {inicio && fim && retorno && (
        <Bloco titulo="Datas calculadas">
          <Linha rotulo="Início do gozo" valor={br(inicio)} />
          <Linha rotulo="Fim do gozo" valor={br(fim)} ajuda={`${dias} dias, contando o primeiro`} />
          <Linha rotulo="Retorno ao trabalho" valor={br(retorno)} />
        </Bloco>
      )}

      {!inicio && (
        <Alerta tom="atencao">
          Informe o início do gozo em dd/mm/aaaa para o aviso trazer as datas de fim e de retorno.
        </Alerta>
      )}

      <BotaoDocumento rotulo="Gerar o aviso" aoClicar={gerar} bloqueado={bloqueado} />
      <p className="text-xs leading-relaxed text-cinza">
        O aviso abre em outra aba, pronto para imprimir e colher a assinatura de ciência. O prazo
        legal é de 30 dias de antecedência (CLT art. 135).
        {ferias && ` O pagamento das férias é de ${reais(ferias.liquido)} líquidos.`}
      </p>
    </div>
  );
}
